// Coalesce concurrent readers and briefly reuse successful, read-only responses.
// Callers retain their own cancellation guards; one unmount must not abort peers.
export function createCachedJSON(fetcher = (...args) => fetch(...args), clock = () => Date.now()) {
  const entries = new Map();
  return function getJSON(url, { ttl = 60000, force = false } = {}) {
    const previous = entries.get(url);
    if (previous?.pending) return previous.pending;
    if (!force && previous?.expires > clock()) return Promise.resolve(previous.value);
    if (previous?.retryAt > clock()) return Promise.reject(previous.error);
    const entry = {};
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    entry.pending = Promise.resolve().then(() => fetcher(url, { signal: controller.signal }))
      .then(async response => {
        if (!response.ok) {
          const error = new Error(`Request unavailable (${response.status})`);
          error.status = response.status;
          if (response.status === 429) {
            const delay = Number(response.headers?.get('retry-after'));
            entry.retryAt = clock() + Math.max(1000, Math.min(60000, Number.isFinite(delay) && delay > 0 ? delay * 1000 : 10000));
            entry.error = error;
          }
          throw error;
        }
        entry.value = await response.json();
        entry.expires = clock() + ttl;
        return entry.value;
      }).catch(error => {
        if (!entry.retryAt && entries.get(url) === entry) entries.delete(url);
        throw error;
      }).finally(() => { clearTimeout(timer); delete entry.pending; });
    entries.set(url, entry);
    // Limit completed records while never disrupting a request another reader awaits.
    if (entries.size > 100) for (const [key, value] of entries) {
      if (entries.size <= 100) break;
      if (!value.pending) entries.delete(key);
    }
    return entry.pending;
  };
}
export const cachedJSON = createCachedJSON();
